/**
 * The sentences the analytics screen owes its reader, kept where a render test
 * can reach them.
 *
 * **They live apart from the page for the reason slice 13 split `ReportCopy.tsx`
 * and slice 12 split its WhatsApp panels.** `AnalyticsPage` imports the API
 * client, which imports `lib/config.ts`, which reads `import.meta.env` and only
 * evaluates inside Vite — so copy that stayed in that file could be rendered by
 * nobody and asserted by nobody. On this screen the copy *is* the deliverable:
 * every number here is defensible and every one of them is misreadable, and the
 * difference between the two is a sentence.
 *
 * Three claims are made here and nowhere else, because each of them is a way
 * this screen could lie while every figure on it stayed correct:
 *
 * **1. The range is half-open, and the end is a boundary rather than a day.**
 * `describeReportWindow` prints both instants and names the zone, spelling the
 * upper bound "up to but not including" — but a reader who picked *30 September*
 * and is shown *1 October* concludes the report is covering a day they did not
 * ask for, or is missing one they did. So the conversion is stated: the last day
 * picked is included in full, and the instant printed after it is the first one
 * that is not. That is also the property that makes two adjacent periods tile —
 * no gap, no overlap, nothing counted twice.
 *
 * **2. The zone is part of the period, not a formatting preference — and it is
 * this device's zone.** Every instant printed here is printed in the range's own
 * zone and the zone is named inside the string. "Last month" read in UTC for a
 * tenant in IST moves five and a half hours of sends into the wrong bucket and
 * produces a screen that looks exactly like a correct one; every partition-dating
 * defect in slices 11 and 12 was this class.
 *
 * Where that zone comes from is the part this screen used to leave out. There is
 * no timezone on `tenants` and none in any contract:
 * `analyticsRangeRequestSchema` refuses to default one and says the caller knows
 * the tenant's zone, and the only caller there is answers with
 * `Intl.DateTimeFormat().resolvedOptions().timeZone` — the **browser's**. So two
 * people in two countries can pick the same two dates off the same workspace and
 * read different campaign lists and different distinct-recipient counts, with no
 * control on the screen to make either match the other. `App.tsx` refuses a
 * `/analytics/:from/:to` route for exactly that divergence; it exists through the
 * device regardless, so the copy names it rather than letting the named zone read
 * as a workspace setting. When `Intl` would not name this browser's zone the
 * screen says that too.
 *
 * **3. A campaign is dated by one instant, so it is in exactly one period.**
 * `campaignInRange` buckets by the campaign's own `sentAt`. A campaign that
 * started at 23:50 on the last day of the range and was still materialising
 * after midnight is entirely inside this period and appears in no other. The
 * rejected alternative — bucketing by the timestamps of the messages a campaign
 * produced — is right for events and wrong for campaigns: it puts one campaign
 * in two periods, and a reader with no way to know they overlap adds the halves.
 *
 * **What is deliberately not claimed.** Nothing here says a period is empty
 * because nothing happened *in the workspace*: `NO_CAMPAIGNS_IN_RANGE_NOTE` is
 * imported rather than re-typed, and it draws the distinction the contract drew
 * — no campaigns went out, which is not the same as a campaign that reached
 * nobody. And nothing here describes a channel a workspace does not use as
 * "zero"; `CHANNEL_NOT_SENDING_NOTES` is the sentence for that, and it is the
 * contract's, so the API's idea of an absent channel and the screen's cannot
 * come apart.
 *
 * **Slice 17 cut this block roughly in half and dropped none of the three
 * claims.** Each one survives as the assertion it makes rather than as the
 * argument behind it: *the last day is included in full and the closing instant
 * is the first one the period does not hold* stayed, while *and that is the
 * property that makes two adjacent periods tile* went, because it is a reason
 * for the design and not a fact about these numbers. Same for the zone — the
 * screen still says the clock is this browser's and not a workspace setting,
 * because a figure whose period depends on who is looking must not be presented
 * as the workspace's; what went is the paragraph explaining that
 * `analyticsRangeRequestSchema` refuses to default one, which is this header's
 * business. Same for the campaign bucketing: *whole, and in no other period*
 * stayed, and the worked 23:50 example went.
 *
 * The test for what stayed was the one the slice was given: would a person
 * reading only this screen draw a false conclusion without it? A reader who does
 * not know the end is exclusive concludes the report covers a day it does not. A
 * reader who does not know the zone is their device's compares their figures
 * with a colleague's and cannot account for the difference. Both stay, and both
 * are visible rather than behind a disclosure, because the reader this slice is
 * for does not open disclosures.
 */
import {
  CHANNEL_NOT_SENDING_NOTES,
  NO_CAMPAIGNS_IN_RANGE_NOTE,
  describeReportWindow,
  type AnalyticsRange,
  type Channel,
} from '@wend/shared';
import { CalendarRange, Info } from 'lucide-react';

/* -------------------------------------------------------------------------- */
/* The period                                                                  */
/* -------------------------------------------------------------------------- */

export interface AnalyticsRangeStatementProps {
  /** The window, or `null` when the zone could not be resolved and none exists. */
  range: AnalyticsRange | null;
  /** The zone the dates are read in — named even when it was a fallback. */
  timezone: string;
  /** False when `Intl` would not name this browser's zone. */
  zoneResolved: boolean;
  /** What this block is describing: a period about to be read, or one on screen. */
  heading: string;
  /**
   * The API's own words for this period — `report.rangeLabel`.
   *
   * Passed when there is a report on screen, and left out while a period is only
   * being *chosen*, because before the request there is no API answer to quote.
   * Both paths produce the same sentence from the same `describeReportWindow`;
   * quoting the response where one exists is what stops the server's idea of the
   * period and the screen's from ever coming apart without anybody noticing.
   */
  label?: string;
}

/**
 * The period, in words, with both boundaries and the zone.
 *
 * Rendered before a read as a preview and again beside the figures afterwards,
 * with the same component both times, so what somebody saw before asking is
 * character-for-character what the screen says the numbers cover.
 */
export function AnalyticsRangeStatement({
  range,
  timezone,
  zoneResolved,
  heading,
  label,
}: AnalyticsRangeStatementProps) {
  return (
    <div className="flex flex-col gap-2 rounded-base border border-line bg-white/[0.02] px-4 py-3.5">
      <span className="flex items-center gap-2 font-mono text-[0.66rem] uppercase tracking-[0.12em] text-muted">
        <CalendarRange aria-hidden="true" className="size-3.5" />
        {heading}
      </span>

      {range === null ? (
        <p className="text-[0.88rem] leading-relaxed text-muted">
          Wend could not work out what those dates mean in {timezone}, so there is no period to
          read. Nothing has been counted against a guessed clock.
        </p>
      ) : (
        <>
          <p className="text-[0.92rem] leading-relaxed text-text">
            {label ?? describeReportWindow(range)}
          </p>

          <p className="text-[0.85rem] leading-relaxed text-muted">
            The last day picked is included in full; the closing instant is the first one this
            period does <span className="text-text">not</span> hold. Both are read in{' '}
            <span className="text-text">{range.timezone}</span> — this browser&rsquo;s clock, not a
            workspace setting.
          </p>

          <p className="text-[0.85rem] leading-relaxed text-muted">
            A campaign belongs whole to the period its send instant falls in, and in no other.
          </p>

          {!zoneResolved && (
            <p className="text-[0.82rem] leading-relaxed text-warn">
              This browser would not name its own timezone, so {timezone} was used instead. Check
              these are the midnights you meant.
            </p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * Said when the period runs past this moment.
 *
 * Not a warning about a mistake — a period that is still running is a perfectly
 * reasonable thing to ask for. It is a warning about *reproducibility*: the same
 * dates read again this evening give different numbers, so two people comparing
 * "the last 30 days" an hour apart are comparing two different periods.
 */
export function AnalyticsOpenPeriodNote() {
  return (
    <p className="flex items-start gap-2 text-[0.85rem] leading-relaxed text-muted">
      <Info aria-hidden="true" className="mt-0.5 size-3.5 shrink-0 text-muted" />
      <span>
        This period has not finished yet, so it holds only what has happened so far. A closed period
        is the one two people can compare.
      </span>
    </p>
  );
}

/* -------------------------------------------------------------------------- */
/* The two absences                                                            */
/* -------------------------------------------------------------------------- */

/** No campaigns in the period — a sentence, never a grid of zeros. */
export function NoCampaignsNote() {
  return (
    <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
      {NO_CAMPAIGNS_IN_RANGE_NOTE}
    </p>
  );
}

/**
 * A channel this workspace does not send on.
 *
 * The contract's own sentence, imported rather than written here, because the
 * distinction it draws is the one this repository keeps re-learning: a tenant
 * with no Meta integration shown "0 delivered on WhatsApp" has been told a
 * measurement was taken. None was.
 */
export function ChannelNotSendingNote({ channel }: { channel: Channel }) {
  return (
    <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
      {CHANNEL_NOT_SENDING_NOTES[channel]}
    </p>
  );
}
